import React, { FC, useEffect } from 'react';

import { Box } from '@mui/system';

import { adminWorkshiftDetails, getWorkshiftDetails } from '@pharmaplan/common';

import genericClasses from '../../../theme/GenericClasses';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import CustomTextField from '../../common/CustomTextField';
import {
  IFromScreen,
  renderScreen,
  setBack,
  setButtons,
  setFooterShow,
  setPagination,
} from '../../../actions/drawerActions';

import AdminLongTermHourlyRates from '../PharmacyList/AdminCreateWorkshift/AdminLongTermHourlyRates';
import LongTermHourlyRates from '../../SelfService/Workshifts/AddWorkshift/LongTermHourlyRates';
import useAdminEditWorkshift from '../../../hooks/Admin/Workshift/useAdminEditWorkshift';
import AllowanceWorkshift from '../../SelfService/Workshifts/AllowanceWorkshift';
import {
  OtherScreens,
  ReplacementLabels,
  ScreenTypes,
} from '../../../helpers/Constants';
import WorkshiftForm from '../../SelfService/Workshifts/WorkshiftForm';
import { useAppSelector } from '../../../hooks/useAppSelector';
import DetailsHeader from '../ApproveBooking/DetailsHeader';
import useDrawer from '../../../hooks/useDrawer';
import strings from '../../../localization';
import useSelfService from '../../../hooks/useSelfService';
import useAdminPermissions from '../../../hooks/Admin/useAdminPermissions';

import AllowanceAdminWorkshift from './AllowanceAdminWorkshift';
import styles from './styles';

interface IEditWorkshift {
  workshiftId: string;
  fromScreen?: IFromScreen;
}

export const zeroHandler = (
  values: { [key: string]: number },
  parser: Array<string>,
) =>
  parser.reduce((acc: Record<string, string | number>, curr) => {
    acc[curr] = values[curr] === 0 ? '' : values[curr];
    return acc;
  }, {});

const { ListOfWorkshifts, AdminReports } = OtherScreens;
const { availableWorkshift } = ScreenTypes;

const EditWorkshift: FC<IEditWorkshift> = ({ workshiftId, fromScreen }) => {
  const dispatch = useAppDispatch();

  const currentWorkshift = useAppSelector(adminWorkshiftDetails);
  const preventBackScreens = [ListOfWorkshifts, AdminReports];

  const { closeDrawer } = useDrawer();
  const { isSelfService } = useSelfService();
  const { canEditRatesOutsideRange } = useAdminPermissions();

  const {
    startDate,
    endDate,
    pharmacyName,
    pharmacyId,
    longTermShiftContractId,
    replacementType,
    bookedWeekDayCost,
    bookedWeekendCost,
    bookedWeekDayHours,
    bookedWeekendHours,
    bookedPharmacyWeekDayCost,
    bookedPharmacyWeekendCost,
  } = currentWorkshift ?? {};

  const isLongTerm = !!longTermShiftContractId;
  const LongTermRateComponent = isSelfService
    ? LongTermHourlyRates
    : AdminLongTermHourlyRates;

  const { formik } = useAdminEditWorkshift(workshiftId);

  useEffect(() => {
    dispatch(getWorkshiftDetails({ id: workshiftId }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workshiftId]);

  const goToAvailableWorkshift = () => {
    if (preventBackScreens.includes(fromScreen as OtherScreens)) {
      closeDrawer();
    } else {
      dispatch(
        renderScreen({
          screenNumber: 1,
          screenType: availableWorkshift,
          date: startDate,
        }),
      );
    }
  };

  useEffect(() => {
    dispatch(setBack(goToAvailableWorkshift));

    dispatch(
      setButtons({
        show: true,
        primaryButton: {
          label: strings.confirm,
          onClick: () => {
            formik.handleSubmit();
          },
        },
        secondaryButton: {
          label: strings.discardChanges,
          onClick: goToAvailableWorkshift,
        },
      }),
    );

    dispatch(setFooterShow(true));
    dispatch(setPagination({ show: false }));

    return () => {
      dispatch(setBack(null));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate]);

  return (
    <Box sx={styles.container}>
      <DetailsHeader
        pharmacyName={pharmacyName}
        startDate={startDate}
        endDate={endDate}
      />

      <WorkshiftForm
        isUpdate
        hideDatePicker
        showRecurrences={false}
        formik={formik}
        isSeries
      />

      {isLongTerm ? (
        <LongTermRateComponent
          bookedWeekdayCost={bookedWeekDayCost}
          pharmacyBookedWeekdayCost={bookedPharmacyWeekDayCost}
          pharmacyBookedWeekendCost={bookedPharmacyWeekendCost}
          bookedWeekendCost={bookedWeekendCost}
          bookedWeekdayHours={bookedWeekDayHours}
          bookedWeekendHours={bookedWeekendHours}
          formik={formik}
          canEditRatesOutsideRange={canEditRatesOutsideRange}
          customWeekdayLabel={
            strings.formatString(
              strings.userWeekdayRate,
              ReplacementLabels()[replacementType],
            ) as string
          }
          customWeekendLabel={
            strings.formatString(
              strings.userWeekendRate,
              ReplacementLabels()[replacementType],
            ) as string
          }
        />
      ) : (
        <AllowanceWorkshift
          pharmacyId={pharmacyId}
          formik={formik}
          isAdmin
          renderHourlyRate={<AllowanceAdminWorkshift formik={formik} />}
        />
      )}

      {isLongTerm && (
        <CustomTextField
          variant="outlined"
          formik={formik}
          id="allowanceNotes"
          placeholder={strings.allowancePlaceholder}
          label={strings.allowances}
          name="allowanceNotes"
          multiline
          maxLength={200}
          customClass={genericClasses.borderNoneTextField}
        />
      )}

      <Box sx={styles.notes}>
        <CustomTextField
          variant="outlined"
          formik={formik}
          id="notes"
          placeholder=" "
          label={strings.notes}
          name="notes"
          multiline
          maxLength={200}
          customClass={genericClasses.borderNoneTextField}
        />
      </Box>
    </Box>
  );
};

export default EditWorkshift;
